import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import MedicalDecor from "../components/MedicalDecor"
import blogPosts from "../data/blogPosts"
import logo from "../asset/images/logo.svg"

const blogAccentMap = {
  'Healthcare Tips': {
    badge: 'text-blue-600',
    hoverHeading: 'group-hover:text-blue-600',
    hoverLink: 'hover:text-blue-600'
  },
  'Technology': {
    badge: 'text-purple-600',
    hoverHeading: 'group-hover:text-purple-600',
    hoverLink: 'hover:text-purple-600'
  },
  'Patient Care': {
    badge: 'text-green-600',
    hoverHeading: 'group-hover:text-green-600',
    hoverLink: 'hover:text-green-600'
  }
}

const Landing = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showCookieBanner, setShowCookieBanner] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)

    try {
      const consent = window.localStorage.getItem('medpool_cookie_consent')
      setShowCookieBanner(consent !== 'accepted')
    } catch (e) {
      setShowCookieBanner(true)
    }

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        isScrolled 
          ? 'border-gray-200 bg-white/98 backdrop-blur-md shadow-sm' 
          : 'border-transparent bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <Link 
              to="/" 
              className="group flex items-center gap-2 transition-all duration-300 hover:scale-105"
            >
              <div className="relative">
                <img 
                  className="h-8 w-8 transition-transform duration-300 group-hover:rotate-6" 
                  src={logo} 
                  alt="Medpool logo" 
                />
                <div className="absolute inset-0 rounded-full bg-black/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              </div>
              <span className="text-2xl font-semibold text-gray-900 transition-colors duration-200 group-hover:text-black">
                Medpool
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 md:flex">
            <div className="flex items-center gap-1">
              <a
                href="#demo"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Use Cases</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
              <a
                href="#pricing"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Pricing</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
              <a
                href="#blog"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Blog</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900 hover:scale-105"
              >
                Login
              </Link>
              <Link
                to="/signup/patient"
                className="group relative overflow-hidden rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-gray-900 hover:shadow-lg hover:scale-105"
              >
                <span className="relative z-10">Get Started</span>
                <span className="absolute inset-0 bg-gradient-to-r from-gray-900 to-black opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg transition-all duration-300 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 py-4 space-y-2 border-t border-gray-200 bg-white">
            <a
              href="#demo"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Use Cases
            </a>
            <a
              href="#pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Pricing
            </a>
            <a
              href="#blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Blog
            </a>
            <div className="pt-2 space-y-2 border-t border-gray-200">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
              >
                Login
              </Link>
              <Link
                to="/signup/patient"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white bg-black rounded-lg text-center transition-all duration-200 hover:bg-gray-900"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-12">
            <div className="mx-auto w-full max-w-2xl">
              <h1 className="text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Connecting Patients with
                <span className="block text-black"> Healthcare Providers</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-gray-600 sm:text-xl">
                Medpool is a modern platform that bridges the gap between patients and hospitals, 
                making medical services more accessible and efficient.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/signup/patient"
                  className="rounded-lg bg-black px-6 py-3 text-center text-base font-semibold text-white transition-colors hover:bg-gray-900"
                >
                  I'm a Patient
                </Link>
                <Link
                  to="/signup/hospital"
                  className="rounded-lg border-2 border-black bg-white px-6 py-3 text-center text-base font-semibold text-black transition-colors hover:bg-gray-50"
                >
                  I'm a Hospital
                </Link>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <MedicalDecor />
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Medpool Use Cases
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See how Medpool works in action
            </p>
          </div>

          <div className="mt-12">
            <div className="mx-auto max-w-5xl">
              {/* Demo Card - Browser Window */}
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                {/* Browser Header */}
                <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="ml-4 flex-1 rounded-md bg-white px-4 py-1.5 text-xs text-gray-600 border border-gray-300">
                      medpool.com/dashboard
                    </div>
                  </div>
                </div>

                {/* Demo Content */}
                <div className="p-8 bg-gradient-to-br from-gray-50 to-white">
                  <div className="space-y-6">
                    {/* Step Indicator */}
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">1</div>
                        <span className="text-xs font-medium text-gray-600">Request</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">2</div>
                        <span className="text-xs font-medium text-gray-600">Select Price</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">3</div>
                        <span className="text-xs font-medium text-gray-600">Payment</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">4</div>
                        <span className="text-xs font-medium text-gray-600">Delivery</span>
                      </div>
                    </div>

                    {/* Request Card */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 1: Create Request</h4>
                      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="mb-3 flex items-center gap-3">
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-800">
                                New Request
                              </span>
                              <span className="text-xs text-gray-500">2 hours ago</span>
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                              Medical Consultation Needed
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              I need a consultation for persistent headaches and dizziness. Looking for a neurologist who can provide a comprehensive evaluation and recommend appropriate treatment options.
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-6 border-t border-gray-100 pt-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="font-medium">3 Hospitals Responded</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">From ₦15,000</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price Comparison Cards */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 2: Select a Price</h4>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="rounded-lg border-2 border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Hospital A
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">₦15,000</div>
                          <div className="mb-4 text-xs text-gray-600">Consultation + Basic Tests</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Same day appointment</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Basic lab tests included</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border-2 border-black bg-gray-50 p-5 shadow-lg transition-all hover:shadow-xl">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Hospital B
                            </div>
                            <span className="rounded-full bg-black px-2 py-0.5 text-xs font-semibold text-white">
                              Selected
                            </span>
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">₦18,500</div>
                          <div className="mb-4 text-xs text-gray-600">Full Package</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Priority scheduling</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Comprehensive tests</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Follow-up consultation</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border-2 border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Hospital C
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">₦22,000</div>
                          <div className="mb-4 text-xs text-gray-600">Premium Service</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>VIP treatment</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Advanced diagnostics</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>24/7 support</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Confirmation Card */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 3: Payment Made</h4>
                      <div className="rounded-lg border-2 border-gray-200 bg-white p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-black text-white">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Payment Successful</h5>
                              <p className="text-xs text-gray-500">Transaction completed securely</p>
                            </div>
                          </div>
                          <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
                            Paid
                          </span>
                        </div>
                        <div className="space-y-3 border-t border-gray-100 pt-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Service Amount</span>
                            <span className="text-sm font-semibold text-gray-900">₦18,500</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Medpool Fee (Fair %)</span>
                            <span className="text-sm font-semibold text-gray-900">₦925</span>
                          </div>
                          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                            <span className="text-sm font-semibold text-gray-900">Total Paid</span>
                            <span className="text-base font-bold text-gray-900">₦18,500</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Payment processed on March 15, 2024 at 2:30 PM</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Status Card */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 4: Service Delivered</h4>
                      <div className="rounded-lg border-2 border-gray-200 bg-white p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-black text-white">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Service Delivered</h5>
                              <p className="text-xs text-gray-500">Medical consultation completed</p>
                            </div>
                          </div>
                          <span className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
                            Delivered
                          </span>
                        </div>
                        <div className="space-y-3 border-t border-gray-100 pt-4">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Hospital B Medical Center</p>
                              <p className="text-xs text-gray-500">123 Healthcare Street, Lagos</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Neurology Consultation</p>
                              <p className="text-xs text-gray-500">Comprehensive evaluation and treatment plan provided</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Delivered on March 16, 2024</p>
                              <p className="text-xs text-gray-500">Service completed at 10:00 AM</p>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <button className="w-full rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 transition-colors">
                              Confirm Received
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Simple & Transparent Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Pay only when you succeed. No upfront costs, no monthly fees, no surprises.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Main Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
              <div className="p-8 sm:p-12">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left Side - Commission Display */}
                  <div className="text-center lg:text-left">
                    <div className="inline-block mb-6">
                      <div className="bg-black rounded-lg p-8 shadow-lg">
                        <div className="text-white">
                          <div className="text-5xl sm:text-6xl font-bold mb-2">Fair %</div>
                          <div className="text-base font-semibold">Commission</div>
                          <div className="text-sm opacity-90 mt-1">Per Transaction</div>
                        </div>
                      </div>
                    </div>

                    <p className="text-base text-gray-700 mb-8 leading-relaxed">
                      A simple, fair commission structure. You only pay when a transaction is completed successfully.
                    </p>

                    {/* Key Benefits */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1 text-sm">Zero Upfront Costs</h4>
                          <p className="text-sm text-gray-600">Join for free. No setup fees or hidden charges.</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1 text-sm">Automatic Processing</h4>
                          <p className="text-sm text-gray-600">Commission is calculated and deducted automatically.</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1 text-sm">Full Feature Access</h4>
                          <p className="text-sm text-gray-600">All platform features included at no extra cost.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Calculation Example */}
                  <div className="lg:border-l lg:border-gray-200 lg:pl-12">
                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-lg font-bold text-gray-900">Example Calculation</h3>
                      </div>

                      <div className="space-y-3">
                        {/* Service Price */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200">
                          <span className="text-gray-700 text-sm font-medium">Service Price</span>
                          <span className="text-base font-bold text-gray-900">₦20,000</span>
                        </div>

                        {/* Commission */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-gray-100 border border-gray-300">
                          <span className="text-gray-700 text-sm font-medium">Medpool Fee (Fair %)</span>
                          <span className="text-base font-bold text-gray-900">₦1,000</span>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-gray-300"></div>

                        {/* You Receive */}
                        <div className="flex items-center justify-between p-4 rounded-lg bg-black border border-gray-900">
                          <span className="text-white text-base font-semibold">You Receive</span>
                          <span className="text-lg font-bold text-white">₦19,000</span>
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 text-center">
                          Commission is automatically calculated and deducted from each successful payment
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom CTA Section */}
                <div className="mt-10 pt-10 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Get Started?</h3>
                      <p className="text-sm text-gray-600">Join thousands of users already on Medpool</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/signup/hospital"
                        className="rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900"
                      >
                        Join as Hospital
                      </Link>
                      <Link
                        to="/signup/patient"
                        className="rounded-lg border-2 border-gray-900 bg-white px-6 py-3 text-center text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50"
                      >
                        Sign Up as Patient
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Latest from Our Blog
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Stay updated with healthcare insights and tips
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map(post => {
              const accent = blogAccentMap[post.category] || {
                badge: 'text-gray-600',
                hoverHeading: 'group-hover:text-gray-900',
                hoverLink: 'hover:text-gray-900'
              }
              return (
                <article
                  key={post.slug}
                  className="group overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
              <div className="relative h-48 overflow-hidden">
                <img 
                      src={post.heroImage}
                      alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => {
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect fill='%23e5e7eb' width='800' height='600'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='24' fill='%239ca3af' text-anchor='middle' dominant-baseline='middle'%3EBlog%3C/text%3E%3C/svg%3E"
                  }}
                />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              </div>
              <div className="p-6">
                    <div className={`text-xs font-semibold uppercase tracking-wide ${accent.badge}`}>
                      {post.category}
                </div>
                    <h3 className={`mt-2 text-lg font-bold text-gray-900 transition-colors duration-300 ${accent.hoverHeading}`}>
                      {post.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                      {post.summary}
                </p>
                <Link
                      to={`/blog/${post.slug}`}
                      className={`mt-4 inline-flex items-center gap-2 text-sm font-semibold text-black transition-colors duration-300 ${accent.hoverLink}`}
                >
                  Read more
                      <svg className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </article>
              )
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              What Our Users Say
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Trusted by patients and hospitals across the country
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Testimonial 1 - Patient */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
              </div>
              
              <div className="relative">
                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  "Medpool made it so easy to find the right hospital for my treatment. I compared prices from multiple hospitals and saved both time and money. The platform is intuitive and the service is excellent."
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                    SA
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Sarah Adebayo</div>
                    <div className="text-sm text-gray-500">Patient, Lagos</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 - Hospital */}
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
              </div>
              
              <div className="relative">
                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  "As a hospital administrator, Medpool has helped us reach more patients and streamline our service delivery. The platform is professional, secure, and has significantly improved our patient acquisition."
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                    DO
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Dr. Okafor</div>
                    <div className="text-sm text-gray-500">Hospital Administrator, Abuja</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 - Patient */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
              </div>
              
              <div className="relative">
                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  "The transparency in pricing is what I love most about Medpool. I can see all available options and make informed decisions. Payment was secure and the service delivery was prompt. Highly recommended!"
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                    MK
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Michael Kalu</div>
                    <div className="text-sm text-gray-500">Patient, Port Harcourt</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed inset-x-0 bottom-0 z-40">
          <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-white/95 shadow-lg border border-gray-200 px-4 py-4 sm:px-6 sm:py-5 backdrop-blur">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-900">
                    We use cookies to improve your experience
                  </p>
                  <p className="text-xs text-gray-600">
                    Medpool uses essential and analytics cookies to keep the platform secure, remember your preferences, and understand how the site is used. You can learn more in our{" "}
                    <Link to="/cookie-policy" className="font-medium text-blue-600 hover:underline">
                      Cookie Policy
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-row items-center justify-end gap-3 mt-1 sm:mt-0 flex-nowrap">
                  <Link
                    to="/privacy-policy"
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline whitespace-nowrap"
                  >
                    Privacy Policy
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        window.localStorage.setItem('medpool_cookie_consent', 'accepted')
                      } catch (e) {
                        // ignore
                      }
                      setShowCookieBanner(false)
                    }}
                    className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-gray-900 whitespace-nowrap"
                  >
                    Accept cookies
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10 lg:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img className="h-9 w-9" src={logo} alt="Medpool logo" />
                <span className="text-xl font-semibold text-gray-900">Medpool</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Connecting patients with healthcare providers through a transparent and efficient marketplace.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Quick Links
              </h3>
              <ul className="space-y-3">
                <li>
                  <a href="#demo" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Use Cases
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* For Users */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                For Users
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/signup/patient" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Patient Signup
                  </Link>
                </li>
                <li>
                  <Link to="/signup/hospital" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Hospital Signup
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Login
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Help Center
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy-policy" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookie-policy" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Medpool. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Landing

